import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaTrash,
} from "react-icons/fa";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { useNavigate } from "react-router-dom";

const TaxTypeMaster = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);

  const [formData, setFormData] = useState({ taxName: "" });
  const [errors, setErrors] = useState({});

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const recordsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ localStorage helper
  const getLS = (key) => localStorage.getItem(key) || "";

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ------------------ Listing API ------------------
  // ------------------ Listing API ------------------
  const fetchTaxTypeList = async (page = 1, searchOverride) => {
    try {
      setLoading(true);

      const search =
        searchOverride !== undefined ? searchOverride : searchTerm;

      const res = await axios.get(`${BASE_URL}/api/tt_taxTypeList`, {
        headers: commonHeaders,
        params: { page, limit: recordsPerPage, search },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load tax type list");
        return;
      }

      const list = res.data.list || [];

      const mapped = list.map((row) => ({
        id: row.fld_taxtypeid,
        sNo: row.rowno,
        taxType: row.fld_taxtype,
        selected: false,
      }));

      setRecords(mapped);
      setTotalPages(res.data.totalPages || 1);
      setTotalRecords(res.data.total || 0);
      setCurrentPage(page);
    } catch (err) {
      console.error("TaxType List Error:", err);
      toast.error("Failed to load tax type list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTaxTypeList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page) => {
    if (!page || page === "..." || page < 1 || page > totalPages || page === currentPage) return;
    fetchTaxTypeList(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    for (let i = start; i <= end; i++) pages.push(i);

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  // ------------------ Form ------------------
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleEditById = async (taxTypeId) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/tt_getTaxTypeById`, {
        headers: commonHeaders,
        params: { taxTypeId },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to fetch record");
        return;
      }

      const d = res.data.data;

      setFormData({ taxName: d.fld_taxtype || "" });
      setIsEdit(true);
      setEditId(taxTypeId);
      setShowFilters(true);
    } catch (err) {
      console.error("Edit fetch error:", err);
      toast.error(err.response?.data?.message || "Server error");
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!String(formData.taxName || "").trim()) {
      newErrors.taxName = "Tax Name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!userId) {
      toast.error("UserId missing! Please login again.");
      return;
    }

    try {
      setSaving(true);

      // ✅ UPDATE MODE
      if (isEdit && editId) {
        const res = await axios.post(
          `${BASE_URL}/api/tt_updateTaxType`,
          {
            taxTypeId: editId,
            taxType: formData.taxName.trim(),
            modifiedBy: Number(userId) || userId,
          },
          {
            headers: commonHeaders,
          }
        );

        if (res.data?.success) {
          toast.success(res.data?.message || "Updated successfully");

          await fetchTaxTypeList(1);

          setIsEdit(false);
          setEditId(null);
          setFormData({ taxName: "" });
          setErrors({});
        } else {
          toast.error(res.data?.message || "Update failed");
        }
        return;
      }

      // ✅ ADD MODE
      const res = await axios.post(
        `${BASE_URL}/api/tt_addTaxType`,
        {
          taxType: formData.taxName.trim(),
          createdBy: Number(userId) || userId,
        },
        {
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        toast.success(res.data?.message || "Saved successfully");

        await fetchTaxTypeList(1);

        setFormData({ taxName: "" });
        setErrors({});
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error("Submit Error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNew = () => {
    setIsEdit(false);
    setEditId(null);
    setErrors({});
    setFormData({ taxName: "" });
    toast.info("Form cleared for new entry");
  };

    // ------------------ Search ------------------
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    fetchTaxTypeList(1, searchTerm);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    fetchTaxTypeList(1, "");
  };

  // ------------------ Checkbox select ------------------
  const handleSelectRecord = (id, checked) => {
    setRecords((prev) =>
      prev.map((r) => (r.id === id ? { ...r, selected: checked } : r))
    );
  };

  const handleRemoveSelected = async () => {
    const selectedIds = records.filter((r) => r.selected).map((r) => r.id);

    if (selectedIds.length === 0) {
      toast.error("Please select records to remove");
      return;
    }

    const ok = window.confirm(`Delete ${selectedIds.length} selected record(s)?`);
    if (!ok) return;

    try {
      const res = await axios.post(
        `${BASE_URL}/api/tt_deleteTaxTypesSelected`,
        {
          ids: selectedIds,
        },
        {
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        toast.success(res.data?.message || "Deleted successfully");
        await fetchTaxTypeList(1);
        return;
      }

      if (res.data?.referredIds?.length) {
        toast.error(
          `Cannot delete. Referred TaxTypeId(s): ${res.data.referredIds.join(", ")}`
        );
      } else {
        toast.error(res.data?.message || "Delete not allowed");
      }
    } catch (err) {
      console.error("Bulk delete error:", err);
      toast.error(err.response?.data?.message || "Server error");
    }
  };

  return (
    <ProjectPreOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TAX TYPE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Tax Type
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalRecords} Records
              </span>
            </div>

                       <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search tax type..."
                  className={`px-3 py-2 rounded-lg border text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
                <button
                  onClick={handleSearchSubmit}
                  className={`px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white text-sm`}
                >
                  Search
                </button>
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className={`px-4 py-2 rounded-lg font-semibold border transition-all duration-200 text-sm ${
                      isDark
                        ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                    }`}
                  >
                    Clear
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Entry Form" : "Show Entry Form"}
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl border mb-6 overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {showFilters && (
              <div
                className={`p-6 border-t ${
                  isDark
                    ? "bg-gray-800 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="max-w-2xl mx-auto space-y-4">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-4">
                      <label
                        className={`text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Tax Name
                      </label>
                    </div>
                    <div className="col-span-8">
                      <input
                        type="text"
                        value={formData.taxName}
                        onChange={(e) =>
                          handleInputChange("taxName", e.target.value)
                        }
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.taxName ? "border-red-500" : ""
                        } ${
                          isDark
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                        placeholder="Enter tax name..."
                      />
                      {errors.taxName && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.taxName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-center gap-4 pt-4">
                    <button
                      onClick={handleSubmit}
                      disabled={saving}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                    >
                      <FaCheck />
                      {saving ? "Submitting..." : "Submit"}
                    </button>

                    <button
                      onClick={handleAddNew}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                    >
                      <FaPlus />
                      Add New
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      S.No
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Tax Type
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">Select</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td
                        colSpan="3"
                        className={`px-4 py-6 text-center text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td
                        colSpan="3"
                        className={`px-4 py-6 text-center text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    records.map((record, index) => (
                      <tr
                        key={record.id}
                        className={`transition-colors duration-200 ${
                          index % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td
                          className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.sNo}
                        </td>

                        <td
                          className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 font-medium ${
                            isDark ? "text-blue-300" : "text-blue-600"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => handleEditById(record.id)}
                            className="underline hover:opacity-80"
                            title="Click to edit"
                          >
                            {record.taxType}
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!record.selected}
                            onChange={(e) =>
                              handleSelectRecord(record.id, e.target.checked)
                            }
                            className={`w-4 h-4 cursor-pointer ${
                              isDark ? "accent-blue-400" : "accent-blue-600"
                            }`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

                    <div className="flex justify-start mt-6">
            <div className="flex gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  currentPage === 1
                    ? isDark
                      ? "bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed"
                      : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Prev
              </button>

              {renderPageNumbers().map((page, index) => (
                <button
                  key={index}
                  onClick={() => handlePageChange(page)}
                  disabled={page === "..."}
                  className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                    page === currentPage
                      ? "bg-blue-600 text-white border-blue-600"
                      : page === "..."
                      ? isDark
                        ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                        : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                      : isDark
                      ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  currentPage === totalPages || totalPages === 0
                    ? isDark
                      ? "bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed"
                      : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={handleRemoveSelected}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-red-700 hover:bg-red-600 text-white"
                  : "bg-red-600 hover:bg-red-700 text-white"
              }`}
            >
              <FaTrash />
              Remove
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default TaxTypeMaster;